import {
  Box,
  Button,
  Checkbox,
  Heading,
  Flex,
  Input,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import MoovenLogo from 'design-system/atoms/mooven-logo';
import FormField from 'design-system/molecules/form-field';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import {
  pathContractorOnboardingExistingUser,
  pathOnboarding,
} from '../../constants/path';
import { useContractorInvite } from '../../data/useContractorInvite';
import {
  DataHandlerFeedback,
  hasDataHandlerFeedBack,
} from '../../utils/dataHandler';

interface FormValues {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  acceptedTerms: boolean;
  orgName: string;
  email: string;
}

function ContractorCreateForm({
  invitedOrgName,
  contactEmail,
  onSubmit,
}: {
  invitedOrgName: string;
  contactEmail: string;
  onSubmit: (values: FormValues) => void;
}) {
  const { formatMessage } = useIntl();
  const {
    register,
    getValues,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      orgName: invitedOrgName,
      password: '',
      confirmPassword: '',
      acceptedTerms: false,
      email: contactEmail,
    },
  });
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const inviteToken = searchParams.get('invite-token');
  const handleSignInClick = () => {
    const newPath = inviteToken
      ? `${pathContractorOnboardingExistingUser()}?invite-token=${encodeURIComponent(inviteToken)}`
      : pathContractorOnboardingExistingUser();

    navigate(newPath);
  };

  const checkConfirmPassword = (
    confirmPassword: FormValues['confirmPassword']
  ) => {
    const formValues = getValues();
    return !(confirmPassword && confirmPassword !== formValues.password);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack gap={8} align="flex-start">
        <VStack gap={4} align="flex-start">
          <Text fontSize="2xl">
            <FormattedMessage
              defaultMessage="Lets get started by creating an account"
              id="LtXUdo"
              description="Create an account"
            />
          </Text>
          <Button
            variant="link"
            onClick={handleSignInClick}
            textDecoration="underline"
          >
            <FormattedMessage
              defaultMessage="Already have an account? Sign in"
              id="JapGs4"
            />
          </Button>
        </VStack>
        <FormField>
          <Input
            focusBorderColor="green.500"
            type="text"
            placeholder="Organisation name"
            {...register('orgName', {
              required: formatMessage({
                defaultMessage: 'Please enter organisation name',
                id: '/3xveu',
                description: 'Error message for organisation name',
              }),
            })}
          />
        </FormField>
        <FormField>
          <Input
            focusBorderColor="green.500"
            type="email"
            placeholder="Email"
            {...register('email', {
              required: formatMessage({
                defaultMessage: 'Please enter email',
                id: 'ggSdvC',
                description: 'Error message for email',
              }),
            })}
          />
        </FormField>
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={4} width="100%">
          <FormField
            isInvalid={!!errors.firstName}
            errorText={errors.firstName?.message}
          >
            <Input
              focusBorderColor="green.500"
              type="text"
              maxLength={35}
              placeholder="First name"
              {...register('firstName', {
                required: formatMessage({
                  defaultMessage: 'Please enter first name',
                  id: 'uBY5EC',
                  description: 'Error message for first name',
                }),
              })}
            />
          </FormField>
          <FormField
            isInvalid={!!errors.lastName}
            errorText={errors.lastName?.message}
          >
            <Input
              focusBorderColor="green.500"
              type="text"
              maxLength={35}
              placeholder="Last name"
              {...register('lastName', {
                required: formatMessage({
                  defaultMessage: 'Please enter last name',
                  id: '8um9H0',
                  description: 'Error message for last name',
                }),
              })}
            />
          </FormField>
        </SimpleGrid>
        <FormField
          isInvalid={!!errors.password}
          errorText={errors.password?.message}
          helperText={formatMessage({
            defaultMessage:
              'Must have at least 8 characters, a lowercase letter, an uppercase letter, a number, and no parts of your email.',
            id: 'Mq+9D/',
            description: 'create account password input helper text',
          })}
        >
          <Input
            focusBorderColor="green.500"
            type="password"
            autoComplete="new-password"
            id="new-password"
            placeholder={formatMessage({
              defaultMessage: 'Password',
              id: 'YXIKM7',
              description: 'Password email input placeholder',
            })}
            {...register('password', {
              required: formatMessage({
                defaultMessage: 'Please enter password',
                id: 'TIrT2w',
                description: 'Error message for password',
              }),
            })}
          />
        </FormField>
        <FormField
          isInvalid={!!errors.confirmPassword}
          errorText={
            errors.confirmPassword?.type === 'checkConfirmPassword'
              ? formatMessage({
                  defaultMessage: 'Please make sure your password matches',
                  id: 'ElIH9F',
                  description: 'Error message for confirm password',
                })
              : errors.confirmPassword?.message
          }
        >
          <Input
            focusBorderColor="green.500"
            type="password"
            autoComplete="new-password"
            placeholder={formatMessage({
              defaultMessage: 'Confirm password',
              id: 'ypxIsX',
              description: 'Confirm password email input placeholder',
            })}
            {...register('confirmPassword', {
              required: formatMessage({
                defaultMessage: 'Please enter confirm password',
                id: 'GJYY+y',
                description: 'Error message for confirm password',
              }),
              validate: {
                checkConfirmPassword,
              },
            })}
          />
        </FormField>
        <FormField
          isInvalid={!!errors.acceptedTerms}
          errorText={errors.acceptedTerms?.message}
        >
          <Checkbox
            colorScheme="green"
            {...register('acceptedTerms', {
              required: true,
            })}
          >
            I agree to the{' '}
            <Link
              isExternal
              href="http://mooven.com/terms"
              onClick={(e) => {
                e.stopPropagation(); // Prevent click from toggling checkbox
              }}
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              isExternal
              href="http://mooven.com/privacy"
              onClick={(e) => {
                e.stopPropagation(); // Prevent click from toggling checkbox
              }}
            >
              Privacy Statement
            </Link>
          </Checkbox>
        </FormField>
        <Button
          isLoading={isSubmitting}
          variant="solid"
          colorScheme="brand"
          type="submit"
          isDisabled={!isValid || isSubmitting}
        >
          <FormattedMessage
            defaultMessage="Create organisation"
            id="HAYy4w"
            description="Create organisation"
          />
        </Button>
      </VStack>
    </form>
  );
}

const ContractorCreateOrg = () => {
  const contractorDatahandler = useContractorInvite();
  const navigate = useNavigate();
  const onSubmit = async (values: FormValues) => {
    await contractorDatahandler.createOrg({
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      password: values.password,
      orgName: values.orgName,
    });
    navigate(pathOnboarding('/set-mfa-device'));
  };
  if (hasDataHandlerFeedBack(contractorDatahandler)) {
    return <DataHandlerFeedback dataHandlersParam={[contractorDatahandler]} />;
  }
  return (
    <Flex bgColor="white" justify="center" align="center" p={8}>
      <VStack width={{ sm: '768px' }} gap={2} align="flex-start">
        <Box w="150px" h="33px">
          <MoovenLogo />
        </Box>
        <Heading fontSize="7xl">
          <FormattedMessage
            defaultMessage="Welcome to Mooven"
            id="r3+oyG"
            description="Welcome to Mooven header"
          />
        </Heading>
        <ContractorCreateForm
          invitedOrgName={contractorDatahandler.data?.invitedOrgName ?? ''}
          contactEmail={contractorDatahandler.data?.invitedOrgEmail ?? ''}
          onSubmit={onSubmit}
        />
      </VStack>
    </Flex>
  );
};

export default ContractorCreateOrg;
